export const space = {
  0: '0',
  0.5: '2px',
  1: '4px',
  1.5: '6px',
  2: '8px',
  2.5: '10px',
  3: '12px',
  3.5: '14px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
} as const;

export const spaceAlias = {
  xs: space[1],
  sm: space[2],
  md: space[3],
  lg: space[4],
  xl: space[6],
  '2xl': space[8],
} as const;

/**
 * Type-size tokens. The `xs`–`hero` keys are the original generic scale.
 * The `display`/`h1`/`h2`/`viewTitle`/`meta`/`eyebrow`/`micro` keys map
 * directly to the Lifty type scale and are the preferred names when the
 * size has a documented role in that system. Both sets coexist so
 * existing consumers don't need a rename.
 */
export const fontSize = {
  // Generic scale
  xs: '12px',
  sm: '13px',
  body: '14px',
  md: '15px',
  lg: '16px',
  xl: '18px',
  hero: '24px',
  // Lifty named steps (see /lifty/type)
  display: '56px',
  h1: '32px',
  h2: '22px',
  viewTitle: '14px',
  meta: '11px',
  eyebrow: '10px',
  micro: '9.5px',
} as const;

export const fontFamily = {
  ui: '"Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
  mono: '"JetBrains Mono", "SF Mono", ui-monospace, Menlo, monospace',
} as const;

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
} as const;

/**
 * Letter-spacing tokens covering the Lifty type scale: from very tight
 * display headings (-1.5px) through neutral body (0) up to wide eyebrow
 * micro labels (0.8px).
 */
export const letterSpacing = {
  tightest: '-1.5px',
  tighter: '-0.6px',
  tight: '-0.3px',
  snug: '-0.1px',
  normal: '0',
  wide: '0.1px',
  wider: '0.4px',
  widest: '0.8px',
} as const;

/**
 * Unitless line-height multipliers. `tight` matches Lifty display,
 * `snug` matches H1/H2, `base` is the standard ratio for compact UI
 * text, `body` is comfortable prose, `loose` is documentation lists.
 */
export const lineHeight = {
  tight: 1.02,
  snug: 1.15,
  base: 1.4,
  body: 1.55,
  loose: 1.7,
} as const;

export const radius = {
  xs: '2px',
  sm: '4px',
  md: '6px',
  lg: '8px',
  card: '10px',
  xl: '12px',
  pill: '999px',
} as const;

export const elevation = {
  flat: { shadow: 'none' },
  raised: { shadow: '0 1px 3px rgba(0,0,0,0.06)' },
  drawer: { shadow: '0 4px 14px rgba(0,0,0,0.06)' },
  floating: { shadow: '0 10px 32px rgba(0,0,0,0.10)' },
  overlay: { shadow: '0 8px 32px rgba(0,0,0,0.15)' },
} as const;

export const transition = {
  color: '120ms ease',
  layout: '180ms ease',
} as const;

export const zIndex = {
  base: 0,
  raised: 10,
  sticky: 100,
  drawer: 200,
  modal: 300,
  popover: 400,
  tooltip: 500,
  toast: 600,
  top: 700,
} as const;

/**
 * Focus ring tokens. `offset` is for external rings (buttons, controls).
 * `insetOffset` is for bordered inputs where the ring replaces the border.
 */
export const focus = {
  width: '2px',
  offset: '2px',
  insetOffset: '-1px',
} as const;

export const motion = {
  pulse: {
    /** Active-state pulse cadence (1.6s) used by StatusBadge active dot. */
    status: '1.6s',
    /** Slower ambient pulse (2s) for low-priority indicators. */
    slow: '2s',
  },
  easing: {
    standard: 'ease',
  },
} as const;
